import {
  enumerateDays,
  socialNetworkTypeIdToChartKey,
} from "./messagesReport"

export const SENTIMENT_CHART_KEYS = [
  "positive",
  "neutral",
  "negative",
  "unknown",
  "mixed",
]

export const SENTIMENT_LABELS = {
  positive: "Positive",
  neutral: "Neutral",
  negative: "Negative",
  unknown: "Unknown",
  mixed: "Mixed",
}

/** API sentiment → chart key; null/empty → unknown. */
export function normalizeSentimentKey(sentiment) {
  if (sentiment == null || String(sentiment).trim() === "") return "unknown"
  const key = String(sentiment).toLowerCase().trim()
  if (SENTIMENT_CHART_KEYS.includes(key)) return key
  return "unknown"
}

function emptySentimentBucket() {
  return { positive: 0, neutral: 0, negative: 0, unknown: 0, mixed: 0 }
}

/**
 * @param {Array<{
 *   messageDay?: string,
 *   messageCount?: number,
 *   socialNetworkTypeId?: number,
 *   sentiment?: string | null,
 * }>} rows
 * @param {Array<{ id?: number, description?: string, name?: string }>} socialNetworkTypes
 * @param {{
 *   startDate?: Date,
 *   endDate?: Date,
 *   platformKeysFilter?: string[] | null,
 *   sentimentLabelFilter?: string | null,
 * }} options
 */
export function buildSentimentChartsFromRows(
  rows,
  socialNetworkTypes = [],
  options = {},
) {
  const safeRows = Array.isArray(rows) ? rows : []
  const { startDate, endDate, platformKeysFilter, sentimentLabelFilter } = options

  const totalsByDay = new Map()
  const totalsBySentiment = emptySentimentBucket()

  for (const row of safeRows) {
    const day = String(row?.messageDay ?? "").slice(0, 10)
    if (!day) continue

    const chartKey = socialNetworkTypeIdToChartKey(
      socialNetworkTypes,
      row?.socialNetworkTypeId,
    )
    if (!chartKey) continue
    if (platformKeysFilter?.length && !platformKeysFilter.includes(chartKey)) continue

    const sentimentKey = normalizeSentimentKey(row?.sentiment)
    if (
      sentimentLabelFilter &&
      sentimentLabelFilter !== "all" &&
      SENTIMENT_LABELS[sentimentKey] !== sentimentLabelFilter
    ) {
      continue
    }

    const count = Number(row?.messageCount) || 0

    if (!totalsByDay.has(day)) totalsByDay.set(day, emptySentimentBucket())
    const dayBucket = totalsByDay.get(day)
    dayBucket[sentimentKey] += count
    totalsBySentiment[sentimentKey] += count
  }

  const totalMessages = SENTIMENT_CHART_KEYS.reduce(
    (sum, key) => sum + totalsBySentiment[key],
    0,
  )

  const dayKeys =
    startDate && endDate
      ? enumerateDays(startDate, endDate)
      : [...totalsByDay.keys()].sort()

  const sentimentSeries = dayKeys.map((date) => ({
    date,
    ...(totalsByDay.get(date) || emptySentimentBucket()),
  }))

  const sentimentShare = SENTIMENT_CHART_KEYS.map((key) => ({
    name: SENTIMENT_LABELS[key],
    value: totalMessages > 0 ? (totalsBySentiment[key] / totalMessages) * 100 : 0,
  }))

  return {
    sentimentSeries,
    sentimentShare,
    sentimentCounts: { ...totalsBySentiment, total: totalMessages },
  }
}
